import { currentUserQueryKey } from '@data/hooks/useCurrentUser'
import { enqueueSnackbar } from '@data/store/slices/unsyncedData/setters'
import { Query, QueryKey } from '@tanstack/react-query'
import anErrorOccurredPleaseTryAgainLaterLabel from '@util/labels/anErrorOccurredPleaseTryAgainLater'
import { logError } from '@util/logger'
import { isAxiosError } from 'axios'
import { throttle } from 'lodash-es'

/**
 * Validates the current user's session after the API returns 401
 *
 * 401 Unauthorized should not normally happen, and it is an indication that our session has been invalidated.
 * When it happens, we invalidate the current user query so that a refetch is invoked. If the refetch also
 * returns a 401, then we perform a full browser reload (see `useCurrentUser.ts`).
 */
async function revalidateSessionOnError(error: unknown) {
    if (!isAxiosError(error) || error.response?.status !== 401) {
        return
    }

    // Import the query client dynamically to avoid a circular dependency
    const queryClient = await import('@data/queries/queryClient').then(({ getQueryClient }) => getQueryClient())

    if (!queryClient.isFetching({ queryKey: currentUserQueryKey })) {
        queryClient.invalidateQueries({ queryKey: currentUserQueryKey })
    }
}

const throttledSnackbar = throttle(enqueueSnackbar, 8000)

/**
 * The `onError` function used as the default for all query hooks.
 * This can be used when you want to have some custom handling for errors in your hook,
 * but you still want to fall back to the default behavior whenever your specific use case
 * does not apply.
 */
export async function defaultOnError(
    error: unknown,
    query?: Query<unknown, unknown, unknown, QueryKey>
): Promise<void> {
    await revalidateSessionOnError(error)

    const {
        silenceErrors,
        silenceErrorsAndSkipLogging,
        errorMessage = anErrorOccurredPleaseTryAgainLaterLabel,
    } = query?.meta ?? {}

    if (
        silenceErrorsAndSkipLogging === true ||
        (typeof silenceErrorsAndSkipLogging === 'function' && silenceErrorsAndSkipLogging(error))
    ) {
        return
    }

    if (silenceErrors === true || (typeof silenceErrors === 'function' && silenceErrors(error))) {
        logError(error, { test: 'console', dev: 'console', localhost: 'console', metadata: { query } })
        return
    }

    throttledSnackbar(errorMessage, { variant: 'error' })
    logError(error, { test: 'console', metadata: { query } })
}
